import React, { useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  Grid,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { pickBy, get, forIn, isEmpty } from 'lodash';
import clientGraphql, { useMutation } from '../../../../common/apollo-graphql';
import { SAVE_CLINIC_MINIMAL_MUTATION } from '../../../clinics/gql/Mutation';
import { HookFormTextField, MyButton } from '../index';
import MobileNumberMask from '../mask/MobileNumberMask';
import CustomDialog from '../CustomDialog';

const initialValues = {
  name: '',
  office_phone: '',
  fax: '',
};

const validationSchema = yup.object().shape({
  name: yup.string().required('Clinic name is required'),
  office_phone: yup
    .string()
    .matches(/.{12,}/, {
      excludeEmptyString: true,
      message: 'Office phone number must have 10 digits',
    })
    .optional(),
  fax: yup
    .string()
    .matches(/.{12,}/, {
      excludeEmptyString: true,
      message: 'Fax must have 10 digits',
    })
    .optional(),
});

export default function AddClinicDialog({ open, onClose, onSuccess, name = null }) {
  const [saveClinic, { loading, error: backendError, reset: resetMutation }] =
    useMutation(SAVE_CLINIC_MINIMAL_MUTATION, {
      client: clientGraphql,
      refetchQueries: ['GetClinics'],
    });

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'all',
  });

  const onSubmit = async (data) => {
    try {
      clearErrors();

      const extraInfo = pickBy(data, (_, key) => ['office_phone', 'fax'].includes(key));
      const parsedData = {
        clinic: {
          name: data.name,
          extra_info: Object.keys(extraInfo).length ? JSON.stringify(extraInfo) : undefined,
        },
      };

      const {
        data: { save_clinic_minimal },
      } = await saveClinic({ variables: parsedData });

      onSuccess(save_clinic_minimal);
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (name) setValue('name', name);
  }, [name, setValue]);

  useEffect(() => {
    if (!open) {
      reset();
      resetMutation();
    }
  }, [open, reset, resetMutation]);

  useEffect(() => {
    if (backendError?.graphQLErrors?.length) {
      const validations = get(backendError, 'graphQLErrors[0].extensions.validation');
      if (!isEmpty(validations)) {
        forIn(validations, (value, key) => {
          setError(key.replace('clinic.', ''), {
            type: 'custom',
            message: value[0],
          });
        });
      }
    }
  }, [backendError, setError]);

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add New Clinic</DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <HookFormTextField
                id="name"
                name="name"
                schema="name"
                label="Clinic name *"
                control={control}
                errors={errors}
                fullWidth
                autoFocus
              />
            </Grid>

            <Grid item container spacing={1}>
              <Grid item xs={12} sm={6}>
                <HookFormTextField
                  id="office_phone"
                  name="office_phone"
                  schema="office_phone"
                  label="Office Phone"
                  control={control}
                  errors={errors}
                  InputProps={{ inputComponent: MobileNumberMask }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <HookFormTextField
                  id="fax"
                  name="fax"
                  schema="fax"
                  label="Fax number"
                  control={control}
                  errors={errors}
                  InputProps={{ inputComponent: MobileNumberMask }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid item container justifyContent="flex-end" spacing={2}>
              <Grid item xs={6}>
                <MyButton onClick={onClose} disabled={loading} fullWidth>
                  Cancel
                </MyButton>
              </Grid>
              <Grid item xs={6}>
                <MyButton
                  type="submit"
                  variant="contained"
                  loading={loading}
                  fullWidth
                >
                  Save Clinic
                </MyButton>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </CustomDialog>
  );
}
